<template>
	<v-app class="webform-v2-app ma-0 pa-0">
		<main
			v-if="formReady"
			ref="previewMain"
			class="webform-v2-main d-flex align-start justify-center"
			:class="{ 'not-in-iframe': !isInIframe }"
			:style="mainStyles">
			<div class="form-container w-100" :class="{ 'not-in-iframe pa-5': !isInIframe }">
				<div class="form-card pa-6" :style="formStyles">
					<!-- Logo -->
					<div v-if="effectiveLogoUrl" class="form-logo mb-5" :class="`logo-${styleConfig.logoPosition}`">
						<img :src="effectiveLogoUrl" alt="Logo" @error="onLogoError" />
					</div>

					<!-- Form Title -->
					<h2
						v-if="!shareUrl && !submitted"
						class="form-title text-break mb-6"
						:style="{ fontWeight: 400 }"
						v-html="sanitizedHeadline"></h2>

					<!-- Subtitle (Header HTML) -->
					<div
						v-if="styleConfig.headerHtml && !shareUrl && !submitted"
						class="form-header-content text-break mb-4"
						v-html="sanitizedHeaderHtml"></div>

					<!-- Partial State: Media was requested but the recording step could not start -->
					<div v-if="mediaError" class="success-message text-center py-6">
						<div class="success-header d-flex align-center justify-center ga-3 mb-6">
							<h3 class="success-title ma-0">Your form was submitted.</h3>
						</div>
						<p class="success-instruction mb-4">
							We received your information, but could not start the recording step. Please contact support for help with your
							recording.
						</p>
					</div>

					<!-- Success State: Form-only (no media requested) -->
					<div v-else-if="submitted && !shareUrl" class="success-message text-center py-6">
						<div class="success-header d-flex align-center justify-center ga-3 mb-6">
							<div class="success-icon d-flex align-center justify-center">✓</div>
							<h3 class="success-title ma-0">Thanks for submitting!</h3>
						</div>
						<p class="success-instruction mb-4">We've received your information and will be in touch soon.</p>
					</div>

					<!-- Success State: Media requested -->
					<div v-else-if="shareUrl" class="success-message text-center py-6">
						<div class="success-header d-flex align-center justify-center ga-3 mb-6">
							<div class="success-icon d-flex align-center justify-center">✓</div>
							<h3 class="success-title ma-0">Your form has been submitted successfully.</h3>
						</div>
						<!-- Desktop: Show QR code with instructions -->
						<div v-if="!isMobileOrTablet" class="success-desktop d-flex flex-column align-center ga-2">
							<p class="success-instruction mb-4">Scan this QR code with your phone to start recording.</p>
							<div class="qr-code-container d-flex justify-center align-center pa-4 ma-2">
								<QRcodeVue :value="shareUrl" :size="200" level="H"></QRcodeVue>
							</div>
						</div>
						<!-- Mobile: Simple confirmation since recording opened in new tab -->
						<div v-else class="success-mobile d-flex flex-column align-center ga-2">
							<p class="success-instruction mb-4">Your recording page has been opened. You can close this tab.</p>
						</div>
					</div>

					<!-- Form -->
					<v-form v-if="!submitted && !shareUrl" ref="formRef" lazy-validation autocomplete="on">
						<div class="form-fields d-flex flex-column">
							<div
								v-for="(field, index) in sortedFields"
								:key="field.id || index"
								class="form-field d-flex flex-column"
								:data-field-id="field.id">
								<label :for="getPublicFormInputAttributes(field).id" class="field-label text-break mb-2">
									{{ field.name }}
									<span v-if="isRequired(field)" class="required-indicator ml-1 font-weight-bold">(Required)</span>
									<span v-else class="font-weight-bold">(Optional)</span>
								</label>

								<div v-if="isPhoneField(field)" class="phone-field-wrapper">
									<!-- .trim (LSP-17337): a v-model modifier normally does nothing on a component -
									     Vue just forwards `modelModifiers` and the child has to honor it - but
									     vue-tel-input v9.8.0 declares it (via defineModel()), so Vue's own emit path
									     applies the trim before formData is assigned. That placement is the point:
									     trimming inside the emit beats writing the model back from @input, which the
									     widget's own re-emit overwrites. Internal spaces ("604 555") are untouched.
									     Note this is plain String.trim(), so it does NOT strip zero-width characters
									     (U+200B et al) - those never reach the model anyway because vueTelProps sets
									     validCharactersOnly, which filters them at the input. -->
									<vue-tel-input
										v-bind="vueTelProps"
										v-model.trim="formData[field.id]"
										:input-options="{
											...vueTelProps.inputOptions,
											placeholder: field.placeholder || '',
											...getPublicFormInputAttributes(field)
										}"
										:class="{ 'phone-error': fieldErrors[field.id] }"
										:autofocus="index === 0 && !isPreviewMode"
										@blur="validateField(field)"
										@input="onPhoneInput(field)" />
								</div>

								<textarea
									v-else-if="field.type === FieldType.TextArea"
									v-model="formData[field.id]"
									class="field-input field-textarea"
									:class="{ 'field-error': fieldErrors[field.id] }"
									:placeholder="field.placeholder || ''"
									rows="3"
									:autofocus="index === 0 && !isPreviewMode"
									:id="getPublicFormInputAttributes(field).id"
									:name="getPublicFormInputAttributes(field).name"
									:autocomplete="getPublicFormInputAttributes(field).autocomplete"
									@blur="validateField(field)"
									@input="clearFieldError(field.id)">
								</textarea>

								<div
									v-else-if="field.type === FieldType.Select"
									class="select-wrapper"
									:ref="getSelectWrapperRef(field.id)">
									<v-select
										:model-value="formData[field.id] || null"
										data-test-id="public-form-select"
										class="form-select"
										:rounded="false"
										:class="{ 'field-error': fieldErrors[field.id] }"
										:items="field.options || []"
										item-title="label"
										item-value="value"
										:placeholder="field.placeholder || 'Select an option'"
										:aria-label="field.name"
										:id="getPublicFormInputAttributes(field).id"
										:name="getPublicFormInputAttributes(field).name"
										:autocomplete="getPublicFormInputAttributes(field).autocomplete"
										:clearable="!isRequired(field) && !!formData[field.id]"
										:autofocus="index === 0 && !isPreviewMode"
										:menu-props="getSelectMenuProps(field.id)"
										@blur="validateField(field)"
										@update:menu="handleSelectMenuUpdate(field.id, $event)"
										@update:model-value="handleSelectModelUpdate(field.id, $event)" />
								</div>

								<div v-else-if="field.type === FieldType.Radio" class="radio-group d-flex flex-column ga-2 mt-1">
									<div v-for="(option, i) in field.options || []" :key="i" class="radio-option d-flex align-center">
										<label class="radio-label d-flex align-center">
											<input
												type="radio"
												:name="field.id"
												:value="option.value"
												v-model="formData[field.id]"
												class="radio-input"
												@change="clearFieldError(field.id)" />
											<span class="radio-custom"></span>
											<span class="radio-text">{{ option.label }}</span>
										</label>
									</div>
									<button
										v-if="!isRequired(field) && formData[field.id]"
										type="button"
										class="clear-radio-btn"
										@click="formData[field.id] = ''">
										Clear selection
									</button>
								</div>

								<div v-else-if="field.type === FieldType.Checkbox" class="checkbox-group d-flex flex-column ga-2 mt-1">
									<div v-for="(option, i) in field.options || []" :key="i" class="checkbox-option d-flex align-center">
										<label class="checkbox-label d-flex align-center">
											<input
												type="checkbox"
												:value="option.value"
												v-model="formData[field.id]"
												class="checkbox-input"
												@change="clearFieldError(field.id)" />
											<span class="checkbox-custom"></span>
											<span class="checkbox-text">{{ option.label }}</span>
										</label>
									</div>
								</div>

								<AddressField
									v-else-if="field.type === FieldType.Address"
									:model-value="formData[field.id]"
									:field="field"
									:form-id="model.id"
									:search-enabled="addressSearchEnabled"
									:preview-mode="isPreviewMode"
									:default-country-code="defaultCountryCode"
									:required="isRequired(field)"
									:error-message="fieldErrors[field.id]"
									:input-attributes="getPublicFormInputAttributes(field)"
									@update:model-value="handleAddressModelUpdate(field, $event)"
									@interaction="handleAddressInteraction(field)"
									@blur="validateField(field)" />

								<!-- Standard input fields (Email, Name, Number, Date, DateTime, Text) -->
								<input
									v-else
									v-model="formData[field.id]"
									:type="getInputType(field)"
									:inputmode="getInputMode(field)"
									class="field-input"
									:class="{ 'field-error': fieldErrors[field.id] }"
									:placeholder="field.placeholder || ''"
									:autofocus="index === 0 && !isPreviewMode"
									:id="getPublicFormInputAttributes(field).id"
									:name="getPublicFormInputAttributes(field).name"
									:autocomplete="getPublicFormInputAttributes(field).autocomplete"
									@blur="validateField(field, $event)"
									@input="handleInput($event, field)"
									@keydown="handleKeydown($event, field)"
									@paste="handlePaste($event, field)" />

								<!-- Field error message. AddressField renders the same message inside its own
								card, so rendering it here too would show the error twice (LSP-19563). -->
								<div
									v-if="fieldErrors[field.id] && field.type !== FieldType.Address"
									class="field-error-message text-break mt-1">
									{{ fieldErrors[field.id] }}
								</div>

								<!-- Character counter — shown whenever a MaxLength is configured (LSP-16444). An Address field
								counts the `formatted` line its length rule bounds, and only once an address has been picked or
								entered, so the empty search state stays clean (LSP-19563). -->
								<div
									v-if="
										getMaxLength(field) !== undefined &&
										!isPhoneField(field) &&
										field.type !== FieldType.Select &&
										field.type !== FieldType.Radio &&
										field.type !== FieldType.Checkbox &&
										field.type !== FieldType.Date &&
										field.type !== FieldType.DateTime &&
										(field.type !== FieldType.Address || !!formData[field.id])
									"
									class="field-char-counter"
									:class="{ 'field-char-counter--over': countFieldCharacters(field) > getMaxLength(field) }"
									:data-test-id="`field-char-counter-${field.id || index}`">
									{{ countFieldCharacters(field) }} / {{ getMaxLength(field) }}
								</div>
							</div>
						</div>

						<!-- Honeypot field (should remain empty). Its wire name and autofill-suppression
						attributes come from publicFormHoneypotAttributes: an autofill-prone name or a
						browser-ignored autocomplete="off" lets Chrome fill it and the API then rejects a
						legitimate submission with a 422 (LSP-19625). -->
						<input
							v-model="honeypotValue"
							v-bind="publicFormHoneypotAttributes"
							type="text"
							tabindex="-1"
							style="position: absolute; left: -9999px; opacity: 0; pointer-events: none"
							aria-hidden="true" />

						<!-- Form ID field (set with JavaScript) -->
						<input type="hidden" name="__fid" :value="formIdValue" />

						<!-- Submit Button -->
						<button
							type="button"
							class="submit-button text-break d-flex align-center justify-center ga-2 mt-2"
							:class="[`button-${styleConfig.buttonStyle}`, `button-radius-${styleConfig.buttonRadius}`]"
							:disabled="saving"
							@click="handleSubmit">
							<span v-if="saving" class="loading-spinner"></span>
							<span v-else>{{ styleConfig.buttonText || defaultButtonText }}</span>
						</button>
					</v-form>

					<!-- Footer HTML -->
					<div
						v-if="styleConfig.footerHtml && !shareUrl && !submitted"
						class="form-footer-content text-break mt-5"
						v-html="sanitizedFooterHtml"></div>
				</div>
			</div>
		</main>

		<!-- Loading state -->
		<div v-else-if="!formError" class="loading-container d-flex align-center justify-center">
			<div class="loading-spinner large"></div>
		</div>

		<!-- Error state -->
		<PageNotFound v-else-if="formError" />

		<!-- Snackbar for errors -->
		<v-snackbar v-model="showSnackbar" top :timeout="5000">
			{{ snackbarMessage }}
		</v-snackbar>
	</v-app>
</template>

<script lang="ts" setup>
	import { onMounted, onBeforeUnmount, ref, computed, reactive, watch, nextTick } from "vue";
	import FormField from "./types/forms/FormField";
	import Form from "./types/forms/Form";
	import { FieldType } from "./types/forms/FieldType";
	import { FieldValidationType } from "./types/forms/FieldValidationType";
	import { FormStyle, DEFAULT_FORM_STYLE, STYLE_VALUES } from "./types/forms/FormStyle";
	import { FormSubmissionField } from "./types/forms/FormSubmissionField";
	import { FormSubmission } from "./types/forms/FormSubmission";
	import {
		formatMaxLengthErrorMessage,
		inheritsDefaultMaxLength,
		isNameCappedField,
		resolvesToEmailField,
		resolvesToPhoneField,
		resolvePublicFormMaxLength,
		VuetifyValidation,
		FIELD_LENGTHS,
		FIELD_NUMBER_BOUNDS,
		DEFAULT_DATE_ERROR
	} from "./helpers/Validation";
	import AddressField from "@/components/publicForm/AddressField.vue";
	import type { AddressValue } from "@/types/forms/AddressValue";
	import { logError, logWarn } from "@/helpers/logError";
	import { loadGoogleFont } from "@/helpers/googleFonts";
	import api from "@/api";
	import constants from "./api/constants";
	import timezones from "./helpers/timezones";
	import { getDateTokens, OrgDateFormat } from "./helpers/dateFormat";
	import { dedupeCountryCodes } from "@/helpers/phone";
	import PageNotFound from "@/components/PageNotFound.vue";
	import DOMPurify from "dompurify";
	import { BASIC_HTML_SANITIZE_OPTIONS, RICH_HTML_SANITIZE_OPTIONS } from "@/helpers/htmlSanitize";
	import QRcodeVue from "qrcode.vue";
	import DeviceUtility from "./helpers/device";
	import {
		filterNumberKeydown,
		getNumberFromClipboard,
		getDiagnosticError,
		getPublicSubmissionFailureDiagnostics
	} from "./helpers/utilities";
	import { countCodePoints } from "@common-ui/utilities/characterCount";
	import { menuProps as globalMenuProps } from "@/plugins/vuetify";
	import {
		getPublicFormInputAttributes as getAutofillInputAttributes,
		PUBLIC_FORM_HONEYPOT_FIELD_NAME,
		publicFormHoneypotAttributes
	} from "@/helpers/publicFormAutofill";

	/** Controls a blocked submission can hand focus to when the field wrapper is the only anchor. */
	const FOCUSABLE_SELECTOR = "input, textarea, select, [tabindex]";

	const formReady = ref(false);
	const formError = ref(false);
	const model = ref<Form>({} as Form);
	const getPublicFormInputAttributes = (field: Partial<FormField>) => getAutofillInputAttributes(field, model.value.FormFields ?? []);
	const organizationLogo = ref<string>();
	const shareUrl = ref("");
	const submitted = ref(false);
	const mediaError = ref(false);
	const saving = ref(false);
	const showSnackbar = ref(false);
	const snackbarMessage = ref("");
	const formData = reactive<Record<string, any>>({});
	const fieldErrors = reactive<Record<string, string>>({});
	// LSP-16446: a native <input type="date"/"datetime-local"> blanks its value on an
	// impossible date (e.g. 2025-04-31) while still showing the typed segments, so the
	// bad value never reaches formData and a model-level rule can't catch it. Track the
	// element's validity.badInput per field so validateField can flag it regardless.
	const dateInputInvalid = reactive<Record<string, boolean>>({});
	const validationRules = new Map<string, Array<(value: any) => string | true>>();
	const honeypotValue = ref("");
	const formIdValue = ref("");
	const isMobileOrTablet = DeviceUtility.isMobile || DeviceUtility.isTablet;
	const previewMain = ref<HTMLElement | null>(null);
	let previewHeightObserver: ResizeObserver | undefined;
	const selectWrapperElements = new Map<string, HTMLElement>();
	const selectWrapperRefs = new Map<string, (element: Element | null) => void>();
	const selectMenuWidths = reactive<Record<string, number>>({});
	let selectWidthObserver: ResizeObserver | undefined;

	const isPreviewMode = computed(() => {
		try {
			return new URLSearchParams(window.location.search).get("preview") === "true";
		} catch {
			return false;
		}
	});

	const isInIframe = computed(() => {
		if (typeof window === "undefined") return false;
		if (isPreviewMode.value) return true;
		const hasEmbedParam = new URLSearchParams(window.location.search).has("embed");
		if (!hasEmbedParam) return false;
		return window.self !== window.top;
	});

	const styleConfig = computed<FormStyle>(() => {
		return { ...DEFAULT_FORM_STYLE, ...model.value.style };
	});

	const effectiveLogoUrl = computed(() => styleConfig.value.logoUrl || organizationLogo.value);

	const defaultButtonText = computed(() => {
		// `!== false` treats undefined/null as media-requesting (the DB default), so
		// forms loaded before requestsMedia existed keep the original button label.
		return model.value.requestsMedia !== false ? "Take a Video and/or Photos" : "Submit";
	});

	const sortedFields = computed(() => {
		if (!model.value.FormFields) return [];
		return [...model.value.FormFields].sort((a, b) => (a.order || 0) - (b.order || 0));
	});

	const mainStyles = computed(() => ({
		"--form-background-color": styleConfig.value.backgroundColor
	}));

	const formStyles = computed(() => {
		const fieldPadding =
			STYLE_VALUES.fieldPadding[styleConfig.value.fieldPadding] ?? STYLE_VALUES.fieldPadding[DEFAULT_FORM_STYLE.fieldPadding];
		const [fieldPaddingBlock, fieldPaddingInline = fieldPaddingBlock] = fieldPadding.split(" ");

		return {
			"--form-primary-color": styleConfig.value.primaryColor,
			"--form-button-text-color": styleConfig.value.buttonTextColor || "#FFFFFF",
			"--form-background-color": styleConfig.value.backgroundColor,
			"--form-text-color": styleConfig.value.textColor,
			"--form-field-background": styleConfig.value.fieldBackgroundColor,
			"--form-field-border-color": styleConfig.value.fieldBorderColor,
			"--form-error-text-color": styleConfig.value.errorTextColor || "#f44336",
			"--form-font-family": `${styleConfig.value.fontFamily}, system-ui, sans-serif`,
			"--form-font-size": STYLE_VALUES.fontSize[styleConfig.value.fontSize],
			"--form-heading-size": STYLE_VALUES.headingSize[styleConfig.value.headingSize],
			"--form-label-font-size": STYLE_VALUES.labelFontSize[styleConfig.value.labelFontSize],
			"--form-footer-font-size": STYLE_VALUES.footerFontSize[styleConfig.value.footerFontSize],
			"--form-field-spacing": STYLE_VALUES.fieldSpacing[styleConfig.value.fieldSpacing],
			"--form-field-padding": fieldPadding,
			"--form-field-padding-block": fieldPaddingBlock,
			"--form-field-padding-inline": fieldPaddingInline,
			"--form-form-border-radius": STYLE_VALUES.formBorderRadius[styleConfig.value.formBorderRadius],
			"--form-border-radius": STYLE_VALUES.borderRadius[styleConfig.value.borderRadius],
			"--form-border-width": STYLE_VALUES.borderWidth[styleConfig.value.borderWidth],
			"--form-button-radius": STYLE_VALUES.buttonRadius[styleConfig.value.buttonRadius]
		};
	});

	const sanitizedHeadline = computed(() => {
		if (!model.value.headline) return "";
		return DOMPurify.sanitize(model.value.headline, BASIC_HTML_SANITIZE_OPTIONS);
	});

	const sanitizedHeaderHtml = computed(() => {
		if (!styleConfig.value.headerHtml) return "";
		return DOMPurify.sanitize(styleConfig.value.headerHtml, RICH_HTML_SANITIZE_OPTIONS);
	});

	const sanitizedFooterHtml = computed(() => {
		if (!styleConfig.value.footerHtml) return "";
		return DOMPurify.sanitize(styleConfig.value.footerHtml, RICH_HTML_SANITIZE_OPTIONS);
	});

	function syncSelectMenuWidth(fieldId: string): void {
		const selectWrapper = selectWrapperElements.get(fieldId);
		const width = selectWrapper?.getBoundingClientRect().width;

		if (width && Number.isFinite(width) && width > 0) {
			selectMenuWidths[fieldId] = Math.ceil(width);
		}
	}

	function syncAllSelectMenuWidths(): void {
		for (const fieldId of selectWrapperElements.keys()) {
			syncSelectMenuWidth(fieldId);
		}
	}

	function getSelectWrapperRef(fieldId: string): (element: Element | null) => void {
		let selectWrapperRef = selectWrapperRefs.get(fieldId);
		if (!selectWrapperRef) {
			// Vue compares function refs by identity. Cache one per field so an unrelated
			// render does not unregister/re-register the wrapper and retrigger width state.
			selectWrapperRef = (element) => registerSelectWrapper(fieldId, element);
			selectWrapperRefs.set(fieldId, selectWrapperRef);
		}

		return selectWrapperRef;
	}

	function registerSelectWrapper(fieldId: string, element: Element | null): void {
		if (!(element instanceof HTMLElement)) {
			const selectWrapper = selectWrapperElements.get(fieldId);
			if (selectWrapper) {
				selectWidthObserver?.unobserve(selectWrapper);
			}
			selectWrapperElements.delete(fieldId);
			selectWrapperRefs.delete(fieldId);
			delete selectMenuWidths[fieldId];
			return;
		}

		selectWrapperElements.set(fieldId, element);
		selectWidthObserver?.observe(element);
		syncSelectMenuWidth(fieldId);
	}

	function getSelectMenuProps(fieldId: string): Record<string, unknown> {
		const width = selectMenuWidths[fieldId];

		// VSelect's default VMenu only uses the activator as a *minimum* width. Its
		// teleported content can still expand for a long option, so fix all dimensions
		// to the measured activator width. Passing menuProps replaces the global defaults,
		// so retain the app's mobile viewport margin and any future menu defaults first.
		return {
			...globalMenuProps,
			...(width
				? {
						width,
						minWidth: width,
						maxWidth: width
					}
				: {}),
			contentClass: "form-select-menu"
		};
	}

	function handleSelectMenuUpdate(fieldId: string, isOpen: boolean): void {
		if (isOpen) {
			syncSelectMenuWidth(fieldId);
		}
	}

	function handleSelectModelUpdate(fieldId: string, value: unknown): void {
		// VSelect emits null when its optional clear control is used. Form submissions
		// represent an unselected single-value field as an empty string.
		formData[fieldId] = value ?? "";
		clearFieldError(fieldId);
	}

	function handleAddressModelUpdate(field: Partial<FormField>, value: AddressValue | null): void {
		if (!field.id) return;

		formData[field.id] = value;
		clearFieldError(field.id);
	}

	// AddressField reports typing/manual edits that do not yet produce a value, so a stale required
	// error clears as soon as the customer engages with the control rather than only on blur.
	function handleAddressInteraction(field: Partial<FormField>): void {
		if (!field.id) return;

		clearFieldError(field.id);
	}

	const defaultCountryCode = ref<string | null>(null);
	const orgDateFormat = ref<OrgDateFormat>("MDY");
	// Whether the org's address search proxy is usable; false makes AddressField open in manual entry.
	const addressSearchEnabled = ref(false);
	const vueTelProps = ref({
		preferredCountries: ["US", "AU", "CA", "GB"],
		mode: "international",
		dropdownOptions: {
			showFlags: true,
			showSearchBox: true,
			tabindex: 0,
			ariaLabel: "Country code"
		},
		inputOptions: {
			placeholder: "",
			autofocus: false,
			tabindex: 0,
			autocomplete: "on"
		},
		disabledFormatting: false,
		validCharactersOnly: true
	});

	watch(
		() => styleConfig.value.fontFamily,
		(newFont) => {
			if (newFont && newFont !== "system-ui") {
				loadGoogleFont(newFont);
			}
		},
		{ immediate: true }
	);

	// Single source of truth for the empty/default value a field's formData entry should hold,
	// keyed by field type. Checkbox holds an array of selected option values; every other type
	// holds a single string ("" renders a Select's placeholder option as the selected value).
	// Both the fresh-load init and the live-preview reconciliation use this so they cannot drift
	// (see LSP-16534, where a field switched away from Checkbox kept a stale [] in only one path).
	function defaultValueForFieldType(type: FieldType | undefined): "" | unknown[] | null {
		// Address holds an AddressValue object once picked, so its empty value is null rather than ""
		// (LSP-19563) — an empty string would read as a picked address with no formatted text.
		if (type === FieldType.Address) return null;
		return type === FieldType.Checkbox ? [] : "";
	}

	// A formData entry only counts as a picked address when it is an object carrying a string
	// `formatted` line (LSP-19563). Used by the live-preview reconciliation to detect a field whose
	// type was switched to or from Address while a value from the previous type was still bound.
	function isAddressValue(value: unknown): boolean {
		return typeof value === "object" && value !== null && typeof (value as AddressValue).formatted === "string";
	}

	function handleMessage(event: MessageEvent) {
		if (event.origin !== window.location.origin) return;

		if (event.data?.type === "formPreviewUpdate") {
			const updatedForm = event.data.form;
			if (updatedForm) {
				model.value = {
					...model.value,
					...updatedForm,
					FormFields: updatedForm.FormFields || model.value.FormFields || []
				};

				for (const field of model.value.FormFields || []) {
					if (field.id) {
						if (formData[field.id] === undefined) {
							formData[field.id] = defaultValueForFieldType(field.type);
						} else if ((field.type === FieldType.Address) !== isAddressValue(formData[field.id])) {
							// LSP-19563: the field was switched TO or AWAY from Address in the builder and the
							// previous type's value is still bound. Switched TO Address, a leftover string is
							// truthy, so AddressField renders an empty "picked" card and the Address length rule
							// reads .formatted off a string; switched AWAY, the stale AddressValue object bound to
							// a native text input renders "[object Object]" — and, on a switch to Checkbox, would
							// be migrated into the array below and submitted as "[object Object]" by the join.
							// This arm therefore runs BEFORE the Checkbox arms so all four directions reset to the
							// new type's default instead of carrying the address across.
							formData[field.id] = defaultValueForFieldType(field.type);
						} else if (field.type === FieldType.Checkbox && !Array.isArray(formData[field.id])) {
							// Switched TO Checkbox: migrate the prior single value into the array shape.
							formData[field.id] = formData[field.id] ? [formData[field.id]] : [];
						} else if (field.type !== FieldType.Checkbox && Array.isArray(formData[field.id])) {
							// LSP-16534: switched AWAY from Checkbox (e.g. to a Dropdown) in the builder.
							// Reset to the single-value default so the stale array doesn't survive — otherwise
							// a Select binds to a truthy [] and its placeholder option isn't shown as selected
							// until a manual refresh re-runs the fresh-load init.
							formData[field.id] = defaultValueForFieldType(field.type);
						}
					}
				}

				setupValidationRules();

				if (model.value.id) {
					formIdValue.value = model.value.id;
				}

				if (!formReady.value) {
					formReady.value = true;
				}

				nextTick(publishPreviewHeight);
			}
		}
	}

	function publishPreviewHeight(): void {
		if (!isPreviewMode.value || !previewMain.value || window.parent === window) {
			return;
		}

		const renderedHeight = Math.max(previewMain.value.scrollHeight, previewMain.value.getBoundingClientRect().height);
		if (!Number.isFinite(renderedHeight) || renderedHeight <= 0) {
			return;
		}

		window.parent.postMessage(
			{
				type: "formPreviewHeight",
				height: Math.max(200, Math.ceil(renderedHeight))
			},
			window.location.origin
		);
	}

	watch(previewMain, (mainElement) => {
		previewHeightObserver?.disconnect();
		previewHeightObserver = undefined;

		if (!isPreviewMode.value || !mainElement) {
			return;
		}

		if (typeof ResizeObserver === "undefined") {
			nextTick(publishPreviewHeight);
			return;
		}

		previewHeightObserver = new ResizeObserver(publishPreviewHeight);
		previewHeightObserver.observe(mainElement);
		nextTick(publishPreviewHeight);
	});

	onMounted(async () => {
		document.body.classList.add("webform-v2-page");
		if (isPreviewMode.value) {
			document.documentElement.classList.add("webform-v2-preview");
			document.body.classList.add("webform-v2-preview");
		}

		window.addEventListener("message", handleMessage);
		if (typeof ResizeObserver !== "undefined") {
			selectWidthObserver = new ResizeObserver(syncAllSelectMenuWidths);
			for (const selectWrapper of selectWrapperElements.values()) {
				selectWidthObserver.observe(selectWrapper);
			}
		}
		window.addEventListener("resize", syncAllSelectMenuWidths);
		nextTick(syncAllSelectMenuWidths);
		if (window.parent && window.parent !== window) {
			window.parent.postMessage({ type: "formPreviewReady" }, window.location.origin);
		}

		try {
			const hash = window.location.hash;
			if (hash) {
				const formId = hash.substring(1).split("?")[0]; // Remove query params from hash
				const response = await api.getFormInsecure(formId);
				model.value = response.form;
				organizationLogo.value = response.organizationLogoUrl;
				defaultCountryCode.value = response.defaultCountryCode || null;
				orgDateFormat.value = (response.dateFormat as OrgDateFormat) || "MDY";
				addressSearchEnabled.value = response.addressSearchEnabled === true;
				if (defaultCountryCode.value) {
					const country = defaultCountryCode.value.toUpperCase();
					const basePreferred = ["US", "AU", "CA", "GB"];
					const filtered = basePreferred.filter((code) => code !== country);
					vueTelProps.value = {
						...vueTelProps.value,
						preferredCountries: dedupeCountryCodes([country, ...filtered])
					};
				}

				for (const field of model.value.FormFields || []) {
					if (field.id) {
						formData[field.id] = defaultValueForFieldType(field.type);
					}
				}

				setupValidationRules();
				formReady.value = true;

				if (model.value.id) {
					formIdValue.value = model.value.id;
				}
			} else if (isPreviewMode.value) {
				formReady.value = true;
			}
		} catch (error: any) {
			if (!isPreviewMode.value) {
				formError.value = true;
				if (error?.code !== 404) {
					console.error("WebFormV2 onMounted error:", error);
				}
			}
		}
	});

	onBeforeUnmount(() => {
		window.removeEventListener("message", handleMessage);
		window.removeEventListener("resize", syncAllSelectMenuWidths);
		document.body.classList.remove("webform-v2-page");
		document.documentElement.classList.remove("webform-v2-preview");
		document.body.classList.remove("webform-v2-preview");
		previewHeightObserver?.disconnect();
		selectWidthObserver?.disconnect();
	});

	function setupValidationRules() {
		validationRules.clear();

		const fieldsWithErrors = Object.keys(fieldErrors);

		for (const key of fieldsWithErrors) {
			delete fieldErrors[key];
		}

		for (const field of model.value.FormFields || []) {
			// Build rules for every identifiable field — not only those with explicit
			// FieldValidators — so the Phone/Name/Number fallbacks below always apply
			// (LSP-16692: a Phone field with no validators still needs the crash guard).
			if (!field.id) continue;
			const fieldValidators = field.FieldValidators || [];

			const rules: Array<(value: any) => string | true> = [];
			let hasValidMaxLength = false;
			let maxLengthErrorTemplate: unknown;
			let hasMaxLengthValidator = false;
			let hasMaxValue = false;
			let hasMinValue = false;
			let hasPhone = false;
			let numberMaxLengthRule: { maxLength: number; errorMessage: string } | undefined;
			let numberLengthRuleInsertionIndex = rules.length;

			if (field.type === FieldType.Date || field.type === FieldType.DateTime) {
				// Reject calendar-invalid dates (e.g. Feb 30) with a specific message — the
				// native date input doesn't guard against impossible dates everywhere (LSP-16446).
				rules.push(VuetifyValidation.DateValidation(DEFAULT_DATE_ERROR).isValid);
			}

			if (field.type === FieldType.Number) {
				rules.push(VuetifyValidation.BasicNumberValidation("Please enter a valid number").isValid);
				const customDecimal = fieldValidators.find(
					(v) => v.type === FieldValidationType.Custom && v.customValidator === "DecimalNumber"
				);
				if (customDecimal) {
					rules.push(VuetifyValidation.DecimalNumberValidation(customDecimal.errorMessageTpl).isValid);
				} else {
					rules.push(VuetifyValidation.DecimalNumberValidation("Up to 2 decimal places").isValid);
				}
				numberLengthRuleInsertionIndex = rules.length;
			}

			for (const validator of fieldValidators) {
				switch (validator.type) {
					case FieldValidationType.Required:
						if (field.type === FieldType.Number) {
							rules.push(VuetifyValidation.NumberRequiredValidationField(validator.errorMessageTpl).isValid);
						} else if (field.type === FieldType.Checkbox) {
							rules.push(VuetifyValidation.RequiredValidationFieldArray(validator.errorMessageTpl).isValid);
						} else if (field.type === FieldType.Select || field.type === FieldType.Radio) {
							rules.push(VuetifyValidation.RequiredValidationFieldObject(validator.errorMessageTpl).isValid);
						} else if (field.type === FieldType.Address) {
							rules.push((value: AddressValue | null) =>
								typeof value?.formatted === "string" && value.formatted.trim().length > 0
									? true
									: validator.errorMessageTpl || `${field.name} is required.`
							);
						} else {
							rules.push(VuetifyValidation.RequiredValidationField(validator.errorMessageTpl).isValid);
						}
						break;
					case FieldValidationType.Email:
						rules.push(
							VuetifyValidation.EmailValidation(
								validator.errorMessageTpl,
								`Email address must not exceed ${FIELD_LENGTHS.EMAIL} characters.`
							).isValid
						);
						break;
					case FieldValidationType.Phone:
						rules.push(VuetifyValidation.PhoneNumberValidation(validator.errorMessageTpl).isValid);
						hasPhone = true;
						break;
					case FieldValidationType.MaxLength: {
						if (hasMaxLengthValidator) {
							logWarn("WebFormV2: dropping duplicate MaxLength validator", {
								fieldId: field.id,
								value: validator.value
							});
							break;
						}
						hasMaxLengthValidator = true;
						maxLengthErrorTemplate = validator.errorMessageTpl;
						// Number fields retain a 15-character safety ceiling while honoring a stricter configured MaxLength.
						const maxLength = resolvePublicFormMaxLength(field, validator);
						if (validator.value != null && maxLength === undefined) {
							logWarn("WebFormV2: dropping MaxLength validator with invalid value", {
								fieldId: field.id,
								value: validator.value
							});
						}
						if (maxLength !== undefined) {
							const effectiveMaxLength = Math.min(maxLength, getMaxLength(field) ?? maxLength);
							const clampedByFieldCeiling = effectiveMaxLength < maxLength;
							// Invariant (LSP-18267): the NUMBER shown is always the effective cap. A stored
							// template is rendered through formatMaxLengthErrorMessage, which substitutes the
							// clamped value, so a 250-worded validator on a 45-capped field still says 45.
							//
							// For non-email fields, wording can still differ: a field whose stored MaxLength is exactly 45 keeps its
							// admin-authored copy, while one clamped down from a looser value gets the generic
							// name copy. That is deliberate — overriding an explicitly configured message would
							// discard copy an admin chose on purpose. Email always uses its canonical length-limit
							// message because stored Email MaxLength configuration is intentionally ignored.
							const errorMessage = resolvesToEmailField(field)
								? `Email address must not exceed ${FIELD_LENGTHS.EMAIL} characters.`
								: field.type === FieldType.Number && clampedByFieldCeiling
									? `Please enter a shorter value (${effectiveMaxLength} characters max).`
									: isNameCappedField(field) && clampedByFieldCeiling
										? `Please enter a shorter name (${effectiveMaxLength} characters max).`
										: formatMaxLengthErrorMessage(validator.errorMessageTpl, field.name, effectiveMaxLength);
							if (field.type === FieldType.Number) {
								numberMaxLengthRule = { maxLength: effectiveMaxLength, errorMessage };
							} else if (field.type !== FieldType.Address) {
								// Address holds an object, not a string, so a string-length rule would measure
								// its stringification rather than the address. Its own rule is pushed below.
								rules.push(VuetifyValidation.MaxCodePointsValidationUntrimmed(effectiveMaxLength, errorMessage).isValid);
							}
							hasValidMaxLength = true;
						}
						break;
					}
					case FieldValidationType.MinLength:
						if (validator.value && !isNaN(Number(validator.value))) {
							rules.push(VuetifyValidation.MinLengthValidation(parseInt(validator.value), validator.errorMessageTpl).isValid);
						}
						break;
					case FieldValidationType.MaxValue:
						// Validator presence signals admin intent — track it regardless of value
						// validity so the safe-ceiling fallback doesn't paper over a misconfig.
						hasMaxValue = true;
						if (validator.value && !isNaN(Number(validator.value))) {
							rules.push(VuetifyValidation.MaxValueValidation(parseInt(validator.value), validator.errorMessageTpl).isValid);
						} else {
							logWarn("WebFormV2: dropping MaxValue validator with invalid value", {
								fieldId: field.id,
								value: validator.value
							});
						}
						break;
					case FieldValidationType.MinValue:
						hasMinValue = true;
						if (validator.value && !isNaN(Number(validator.value))) {
							rules.push(VuetifyValidation.MinValueValidation(parseInt(validator.value), validator.errorMessageTpl).isValid);
						} else {
							logWarn("WebFormV2: dropping MinValue validator with invalid value", {
								fieldId: field.id,
								value: validator.value
							});
						}
						break;
					case FieldValidationType.Custom:
						if (validator.customValidator === "DecimalNumber") {
							// Already handled above for Number fields
							if (field.type !== FieldType.Number) {
								rules.push(VuetifyValidation.DecimalNumberValidation(validator.errorMessageTpl).isValid);
							}
						} else if (validator.customValidator === "FirstAndLastName") {
							rules.push(VuetifyValidation.NameRule(validator.errorMessageTpl).isValid);
						}
						break;
				}
			}
			// Email-typed fields must also validate when no explicit Email rule was stored.
			if (field.type === FieldType.Email && !fieldValidators.some((validator) => validator.type === FieldValidationType.Email)) {
				rules.push(
					VuetifyValidation.EmailValidation(
						"Please enter a valid email address.",
						`Email address must not exceed ${FIELD_LENGTHS.EMAIL} characters.`
					).isValid
				);
			}
			// Fallback (LSP-16692): a Phone-type field always gets the length-guarded phone
			// validation, even when the admin configured no explicit Phone validator. Without
			// this, an oversized paste into such a field would have no rule to reject it.
			if (field.type === FieldType.Phone && !hasPhone) {
				rules.push(VuetifyValidation.PhoneNumberValidation("Please enter a valid phone number.").isValid);
			}
			// Fallback: enforce max-length on Name fields if no explicit MaxLength validator is configured
			if (isNameCappedField(field)) {
				if (!hasValidMaxLength) {
					rules.push(
						VuetifyValidation.MaxCodePointsValidationUntrimmed(
							FIELD_LENGTHS.NAME,
							`Please enter a shorter name (${FIELD_LENGTHS.NAME} characters max).`
						).isValid
					);
				}
			}
			// Fallback (LSP-16444): enforce the same API-injected default the counter shows, so the
			// builder preview turns red past the limit up-front. See inheritsDefaultMaxLength.
			// resolvesToEmailField only, NOT resolvesToPhoneField, and that asymmetry is deliberate
			// (LSP-18267): an email input uses its 254-character ceiling, whereas a phone field is
			// bounded by PhoneNumberValidation's own
			// FIELD_LENGTHS.PHONE_INPUT limit and renders no character counter in either variant, so a
			// generic 250 length rule would be dead weight rather than a guard.
			if (!hasValidMaxLength && (inheritsDefaultMaxLength(field) || resolvesToEmailField(field))) {
				const fallbackMaxLength = resolvesToEmailField(field) ? FIELD_LENGTHS.EMAIL : FIELD_LENGTHS.FORM_FIELD_DEFAULT_MAX;
				const fallbackMessage = resolvesToEmailField(field)
					? `Email address must not exceed ${fallbackMaxLength} characters.`
					: formatMaxLengthErrorMessage(maxLengthErrorTemplate, field.name, fallbackMaxLength);
				rules.push(VuetifyValidation.MaxCodePointsValidationUntrimmed(fallbackMaxLength, fallbackMessage).isValid);
			}
			if (field.type === FieldType.Address) {
				// An Address field holds an object, so the generic string length fallbacks above cannot
				// bound it. Guard the formatted line the API stores against the same default ceiling.
				// Deliberately FORM_FIELD_DEFAULT_MAX: Address exposes no MaxLength UI in the builder, so
				// there is no stored per-field limit to honour — if that UI ever ships, a stricter stored
				// MaxLength must win over this default.
				rules.push((value: AddressValue | null) =>
					typeof value?.formatted !== "string" || countCodePoints(value.formatted) <= FIELD_LENGTHS.FORM_FIELD_DEFAULT_MAX
						? true
						: `${field.name} must not exceed ${FIELD_LENGTHS.FORM_FIELD_DEFAULT_MAX} characters.`
				);
			}
			if (field.type === FieldType.Number) {
				// Keep number-format feedback first, then report the enforced character cap before
				// any numeric min/max rule when a value violates both constraints (LSP-19451).
				const numberLengthRule = numberMaxLengthRule ?? {
					maxLength: FIELD_LENGTHS.NUMBER,
					errorMessage: `Please enter a shorter value (${FIELD_LENGTHS.NUMBER} characters max).`
				};
				rules.splice(
					numberLengthRuleInsertionIndex,
					0,
					VuetifyValidation.MaxCodePointsValidationUntrimmed(numberLengthRule.maxLength, numberLengthRule.errorMessage).isValid
				);
				if (!hasMaxValue) {
					rules.push(
						VuetifyValidation.MaxValueValidation(
							FIELD_NUMBER_BOUNDS.MAX,
							`Please enter a number less than or equal to ${FIELD_NUMBER_BOUNDS.MAX}.`
						).isValid
					);
				}
				if (!hasMinValue) {
					rules.push(
						VuetifyValidation.MinValueValidation(
							FIELD_NUMBER_BOUNDS.MIN,
							`Please enter a number greater than or equal to ${FIELD_NUMBER_BOUNDS.MIN}.`
						).isValid
					);
				}
			}

			validationRules.set(field.id, rules);
		}

		for (const field of model.value.FormFields || []) {
			if (field.id && fieldsWithErrors.includes(field.id)) {
				validateField(field);
			}
		}
	}

	/**
	 * True only for a field that is genuinely a name input, and therefore subject to the
	 * FIELD_LENGTHS.NAME ceiling (LSP-18267).
	 *
	 * Keyed on VALIDATOR PRESENCE, never on `field.type` alone, because a Name-typed field that
	 * also carries an Email or Phone validator is treated as an email/phone input by the rest of
	 * the form — WebForm.vue even reassigns `field.type` in place while building its rules. Reading
	 * the mutable type would make the ceiling depend on the order validators happen to appear in
	 * (clamped if MaxLength is processed before Email, not clamped after) and would make the two
	 * WebForm variants disagree, since WebFormV2.vue never mutates the type and instead resolves
	 * the same thing through isEmailField/isPhoneField. An email input must keep its own longer
	 * limit rather than being squeezed into 45.
	 */
	/**
	 * Returns the configured MaxLength for a field so a live character counter can
	 * be shown before the user exceeds the limit (LSP-16444).
	 * Returns undefined for fields without a MaxLength validator (no counter shown).
	 */
	/**
	 * Counts what the field's length rule actually bounds. Every other field type stores a string, but
	 * an Address stores an AddressValue whose `formatted` line is the value the rule and the API see.
	 */
	function countFieldCharacters(field: Partial<FormField>): number {
		const value = formData[field.id as string];
		if (field.type === FieldType.Address) return countCodePoints((value as AddressValue | null)?.formatted ?? "");
		return countCodePoints(value);
	}

	function getMaxLength(field: Partial<FormField>): number | undefined {
		// Address exposes no MaxLength UI in the builder, so its counter mirrors exactly what the Address
		// length rule in setupValidationRules enforces — the default ceiling — rather than a stored
		// validator that rule would ignore. If that UI ever ships, both places change together.
		if (field.type === FieldType.Address) {
			return FIELD_LENGTHS.FORM_FIELD_DEFAULT_MAX;
		}
		// Number fields retain a 15-character ceiling (LSP-16444, per Oliver), while a stricter stored
		// MaxLength takes precedence. The API may inject generic MaxLength:250 after save; cap it to 15
		// so the counter remains stable. setupValidationRules enforces the same rule.
		const maxLengthValidator = field.FieldValidators?.find((v) => v.type === FieldValidationType.MaxLength);
		const configuredMaxLength = resolvePublicFormMaxLength(field, maxLengthValidator);
		if (configuredMaxLength !== undefined) {
			if (field.type === FieldType.Number) {
				return Math.min(configuredMaxLength, FIELD_LENGTHS.NUMBER);
			}
			// A Name field keeps its own ceiling the same way a Number field does (LSP-18267). The API
			// injects a generic MaxLength:250 into every field on save (contact-api controllers/forms.ts)
			// and getDefaultLayout ships the Name field with 250, which used to WIDEN the Name cap past
			// FIELD_LENGTHS.NAME — so 45 only ever applied to forms that carried no validator at all. A
			// stricter stored MaxLength still wins; a looser one can no longer raise the ceiling.
			if (isNameCappedField(field)) {
				return Math.min(configuredMaxLength, FIELD_LENGTHS.NAME);
			}
			return configuredMaxLength;
		}
		if (field.type === FieldType.Number) {
			return FIELD_LENGTHS.NUMBER;
		}
		// Fallback for Name fields mirrors the setupValidationRules fallback
		if (isNameCappedField(field)) {
			return FIELD_LENGTHS.NAME;
		}
		// Preview parity (LSP-16444): show the API-injected default up-front so a freshly-added
		// field isn't counter-less until the save round-trip. See inheritsDefaultMaxLength.
		// Email only, not phone — see the note in the rule builder above (LSP-18267).
		if (resolvesToEmailField(field)) {
			return FIELD_LENGTHS.EMAIL;
		}
		if (inheritsDefaultMaxLength(field)) {
			return FIELD_LENGTHS.FORM_FIELD_DEFAULT_MAX;
		}
		return undefined;
	}

	function isRequired(field: Partial<FormField>): boolean {
		return !!field.FieldValidators?.some((v) => v.type === FieldValidationType.Required);
	}

	// Delegates to the shared predicate (LSP-18267) so V1 and V2 cannot drift apart.
	function isEmailField(field: Partial<FormField>): boolean {
		return resolvesToEmailField(field);
	}

	// Delegates to the shared predicate (LSP-18267).
	function isPhoneField(field: Partial<FormField>): boolean {
		return resolvesToPhoneField(field);
	}

	function getInputType(field: Partial<FormField>): string {
		if (isEmailField(field)) {
			return "email";
		}
		// Use "text" instead of "number" so voice-typed alphabetic characters are preserved in .value
		// and caught by DecimalNumberValidation, rather than bypassing the browser's type="number" constraint.
		if (field.type === FieldType.Number) {
			return "text";
		}
		if (field.type === FieldType.Date || field.type === FieldType.DateTime) {
			return "date";
		}
		return "text";
	}

	function getInputMode(field: Partial<FormField>): "decimal" | undefined {
		if (field.type === FieldType.Number) {
			return "decimal";
		}
		return undefined;
	}

	function handleInput(event: Event, field: Partial<FormField>): void {
		if (!field.id) return;
		if (field.type === FieldType.Date || field.type === FieldType.DateTime) {
			// The browser rejects an impossible date by emptying the value but sets
			// validity.badInput; remember it so validateField surfaces the error even
			// though formData[field.id] is "".
			const input = event.target as HTMLInputElement | null;
			dateInputInvalid[field.id] = input?.validity?.badInput === true;
		}
		clearFieldError(field.id);
	}

	function handlePaste(event: ClipboardEvent, field: Partial<FormField>): void {
		if (field.type === FieldType.Number && field.id) {
			event.preventDefault();
			formData[field.id] = getNumberFromClipboard(event, true);
			clearFieldError(field.id);
		}
	}

	function handleKeydown(event: KeyboardEvent, field: Partial<FormField>): void {
		if (field.type === FieldType.Number) {
			filterNumberKeydown(event);
		}
	}

	function validateField(field: Partial<FormField>, event?: Event): boolean {
		if (!field.id) return true;

		// A native date/datetime input that rejected an impossible entry (validity.badInput)
		// leaves formData empty, so the model-level rules below can't see it. Refresh the flag
		// from the element when we have it (blur), then flag the field regardless of source.
		if (field.type === FieldType.Date || field.type === FieldType.DateTime) {
			const input = event?.target as HTMLInputElement | null;
			if (input?.validity) {
				dateInputInvalid[field.id] = input.validity.badInput === true;
			}
			if (dateInputInvalid[field.id]) {
				fieldErrors[field.id] = DEFAULT_DATE_ERROR;
				return false;
			}
		}

		const rules = validationRules.get(field.id) || [];
		const value = formData[field.id];

		for (const rule of rules) {
			const result = rule(value);
			if (result !== true) {
				fieldErrors[field.id] = result;
				return false;
			}
		}

		delete fieldErrors[field.id];
		return true;
	}

	function clearFieldError(fieldId: string) {
		delete fieldErrors[fieldId];
	}

	/**
	 * Validates every field and returns the invalid ones in display (sorted) order — an empty
	 * array means the form is submittable.
	 *
	 * It returns the list rather than a bare boolean because handleSubmit has to name the offending
	 * fields in its diagnostic log and scroll the FIRST one into view (LSP-20079). A boolean told
	 * the caller only that something failed, which is exactly the information the customer was
	 * missing too.
	 */
	function validateAllFields(): Partial<FormField>[] {
		const invalidFields: Partial<FormField>[] = [];

		for (const field of sortedFields.value) {
			if (!validateField(field)) {
				invalidFields.push(field);
			}
		}

		return invalidFields;
	}

	/**
	 * Resolves the DOM node for a field: preferentially the input itself (the same id the field's
	 * label points at), falling back to the field's wrapper for controls that have no single input
	 * id — Radio and Checkbox groups render one input per option.
	 */
	function findFieldElement(field: Partial<FormField>): HTMLElement | null {
		if (!field.id || typeof document === "undefined") return null;

		const input = document.getElementById(getPublicFormInputAttributes(field).id);
		if (input) return input;

		const wrappers = Array.from(document.querySelectorAll<HTMLElement>(".form-field[data-field-id]"));
		return wrappers.find((wrapper) => wrapper.getAttribute("data-field-id") === field.id) ?? null;
	}

	/**
	 * Scrolls the first invalid field into view and focuses it (LSP-20079).
	 *
	 * On a long form the failing field is often scrolled far above the submit button, so a blocked
	 * submission looked like a dead button — a customer tapped it 15 times. Every DOM call is
	 * feature-detected so an embed or test environment without smooth scrolling never throws and
	 * silently loses the submission attempt.
	 */
	async function revealFirstInvalidField(field: Partial<FormField> | undefined): Promise<void> {
		if (!field?.id) return;

		// The field-error message renders on the same tick the errors are set; wait for it so the
		// element is measured (and centred) at its final height.
		await nextTick();

		const element = findFieldElement(field);
		if (!element) {
			logWarn("WebFormV2: could not resolve an element for the first invalid field", {
				formId: model.value.id,
				fieldId: field.id,
				fieldName: field.name
			});
			return;
		}

		if (typeof element.scrollIntoView === "function") {
			element.scrollIntoView({ behavior: "smooth", block: "center" });
		}

		const focusTarget = element.matches(FOCUSABLE_SELECTOR) ? element : element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
		if (typeof focusTarget?.focus === "function") {
			focusTarget.focus({ preventScroll: true });
		}
	}

	// Phone handlers
	function onPhoneInput(field?: Partial<FormField>) {
		const phoneField = field ?? sortedFields.value.find((f) => isPhoneField(f));
		if (!phoneField?.id) return;

		// Crash guard (LSP-16692): when the entry exceeds the sane phone-input ceiling,
		// surface the error immediately instead of waiting for blur, so an oversized
		// paste turns the field red right away (and blocks submit) rather than sitting
		// in the form as an unbounded value. Typing is never silenced — we only validate.
		if (String(formData[phoneField.id] ?? "").length > FIELD_LENGTHS.PHONE_INPUT) {
			validateField(phoneField);
			return;
		}

		// Otherwise clear the phone field error on input.
		clearFieldError(phoneField.id);
	}

	function onLogoError(event: Event) {
		const img = event.target as HTMLImageElement;
		img.style.display = "none";
	}

	/**
	 * Every early return here logs why it fired (CLAUDE.md "Diagnostic logging when fixing silent
	 * failures"): all three used to return silently, so a submission that never happened was
	 * indistinguishable from a broken button in the field.
	 */
	function handleSubmit() {
		if (isPreviewMode.value) {
			logWarn("WebFormV2: submit ignored because the form is rendered in preview mode", { formId: model.value.id });
			return;
		}
		if (shareUrl.value || submitted.value) {
			logWarn("WebFormV2: submit ignored because this form was already submitted", {
				formId: model.value.id,
				hasShareUrl: Boolean(shareUrl.value),
				submitted: submitted.value
			});
			return;
		}

		const invalidFields = validateAllFields();
		if (invalidFields.length > 0) {
			logWarn("WebFormV2: submit blocked by field validation", {
				formId: model.value.id,
				invalidFieldIds: invalidFields.map((field) => field.id),
				invalidFieldNames: invalidFields.map((field) => field.name)
			});
			// Fire and forget: submitForm must stay in the synchronous user-gesture chain on the
			// valid path, so only the blocked path awaits a tick.
			void revealFirstInvalidField(invalidFields[0]);
			return;
		}

		submitForm();
	}

	async function submitForm() {
		if (isPreviewMode.value) return;

		saving.value = true;
		const requestsMedia = model.value.requestsMedia !== false;
		let mobileTab: Window | null = null;

		try {
			// Pre-open a blank tab on mobile BEFORE the async API call.
			// This must happen synchronously in the user gesture chain;
			// otherwise mobile browsers block the popup.
			// Only open a tab if this form requests media (photos/videos).
			if (isMobileOrTablet && requestsMedia) {
				mobileTab = window.open("about:blank", "_blank");
			}

			const fieldSubmissions: FormSubmissionField[] = [];

			for (const field of sortedFields.value) {
				if (!field.id) continue;

				let value = formData[field.id];
				let type = FieldType.Text;

				if (isPhoneField(field)) {
					type = FieldType.Phone;
				} else if (field.type === FieldType.Date) {
					type = FieldType.Date;
				} else if (field.type === FieldType.DateTime) {
					type = FieldType.DateTime;
				} else if (field.type === FieldType.Number) {
					type = FieldType.Number;
				} else if (field.type === FieldType.Email || isEmailField(field)) {
					type = FieldType.Email;
				} else if (field.type === FieldType.Name) {
					type = FieldType.Name;
				} else if (field.type === FieldType.Select || field.type === FieldType.Radio) {
					type = FieldType.Text;
				} else if (field.type === FieldType.Checkbox) {
					type = FieldType.Text;
					if (Array.isArray(value)) {
						value = value.join(", ");
					}
				}

				if (field.type === FieldType.Address) {
					// An Address saves as a single plain-text line (LSP-19563). Nothing structured is
					// stored: the picked details only ever live in the browser.
					value = (value as AddressValue | null)?.formatted ?? "";
					type = FieldType.Text;
				}

				fieldSubmissions.push({
					fieldId: field.id,
					type,
					value
				} as FormSubmissionField);
			}

			fieldSubmissions.push({
				fieldId: PUBLIC_FORM_HONEYPOT_FIELD_NAME,
				type: FieldType.Text,
				value: honeypotValue.value
			} as FormSubmissionField);

			fieldSubmissions.push({
				fieldId: "__fid",
				type: FieldType.Text,
				value: formIdValue.value
			} as FormSubmissionField);

			const nameField = model.value.FormFields?.find((f) => constants.areEqual(f.name, "Name"));
			const currentDate = timezones.formatDate(new Date().toISOString(), getDateTokens(orgDateFormat.value).dateShortMonth);
			let conversationName = "";

			if (nameField?.id && formData[nameField.id]) {
				const firstName = formData[nameField.id].split(" ")[0];
				conversationName = requestsMedia
					? `Recording from ${firstName} on ${currentDate}`
					: `Submission from ${firstName} on ${currentDate}`;
			}

			const formSubmission: Partial<FormSubmission> = {
				formId: model.value.id,
				organizationId: model.value.organizationId,
				FormSubmissionFields: fieldSubmissions,
				forcedUserId: model.value.createdBy
			};

			if (conversationName) {
				(formSubmission as any).conversationName = conversationName;
			}

			const response = await api.submitCustomWebForm(formSubmission);

			if (requestsMedia && response.customerUrl) {
				shareUrl.value = response.customerUrl;
			} else if (requestsMedia) {
				// Backend accepted the submission but did not return a customerUrl,
				// so the user cannot continue to the media-capture step they expected.
				// Flag this as a distinct error state instead of silently showing the
				// non-media "thanks" message, which would be misleading.
				mediaError.value = true;
				submitted.value = true;
				console.warn("WebFormV2: requestsMedia is enabled but customerUrl is missing", { formId: model.value.id });
				if (mobileTab) {
					mobileTab.close();
					mobileTab = null;
				}
			} else {
				submitted.value = true;
			}

			if (window.parent !== window) {
				try {
					const submittedFields = fieldSubmissions
						.filter((f) => !f.fieldId.startsWith("__"))
						.map((f) => {
							const fieldDef = sortedFields.value.find((sf) => sf.id === f.fieldId);
							return {
								name: fieldDef?.name,
								type: f.type,
								value: f.value
							};
						});

					window.parent.postMessage(
						{
							type: "LS_FORM_SUBMIT",
							formId: model.value.id,
							formName: model.value.headline,
							fields: submittedFields,
							timestamp: new Date().toISOString()
						},
						"*"
					);
				} catch (err) {
					console.warn("WebFormV2: postMessage to parent failed", err);
				}
			}

			if (shareUrl.value) {
				if (isMobileOrTablet) {
					if (mobileTab) {
						try {
							mobileTab.location.href = shareUrl.value;
						} catch (tabErr) {
							// Tab was closed by a popup blocker during the async call — fall through to direct navigation.
							try {
								window.top!.location.href = shareUrl.value;
							} catch (topErr) {
								console.warn("WebFormV2: mobileTab and window.top navigation failed, falling back to window.location", {
									tabErr,
									topErr
								});
								window.location.href = shareUrl.value;
							}
						}
					} else {
						try {
							// .href setter is cross-origin writable per the HTML spec,
							// unlike .assign() which throws cross-origin.
							// Use window.top to navigate the outermost frame (the customer's page).
							window.top!.location.href = shareUrl.value;
						} catch (topErr) {
							console.warn("WebFormV2: window.top navigation failed, falling back to window.location", topErr);
							window.location.href = shareUrl.value;
						}
					}
					return;
				}

				if (navigator.clipboard) {
					try {
						await navigator.clipboard.writeText(shareUrl.value);
					} catch {
						console.warn("Failed to copy link to clipboard");
					}
				}
			}
		} catch (err: any) {
			if (mobileTab) {
				mobileTab.close();
			}
			if (err?.httpStatus === 404) {
				logError("WebFormV2: public form submission returned 404", getDiagnosticError(err), {
					formId: model.value.id,
					httpStatus: err?.httpStatus,
					code: err?.code
				});
				snackbarMessage.value = "Sorry, this form has been deleted and can no longer be used.";
			} else {
				const isTrustedApiValidation =
					err?.httpStatus >= 400 && err?.httpStatus < 500 && err?.isJson === true && err?.userFacing === true;
				const message = isTrustedApiValidation && typeof err?.message === "string" && err.message.trim() ? err.message : undefined;
				if (!message) {
					const diagnostics = getPublicSubmissionFailureDiagnostics(err);
					logError("WebFormV2: using generic public submission error", diagnostics.error, {
						formId: model.value.id,
						...diagnostics.context
					});
				}
				snackbarMessage.value = message || "An error occurred while submitting the form. Please try again.";
			}
			showSnackbar.value = true;

			console.error("WebFormV2 submitForm error:", err, { formId: model.value.id });
		} finally {
			saving.value = false;
		}
	}
</script>

<style lang="scss">
	/* Reset default margins for embedded forms - scoped to webform-v2-page */
	html.webform-v2-page,
	body.webform-v2-page,
	body.webform-v2-page #app,
	body.webform-v2-page .v-application {
		margin: 0 !important;
		padding: 0 !important;
		border: 0 !important;
		background-color: transparent !important;
	}

	body.webform-v2-page {
		width: 100% !important;
		height: 100% !important;
		overflow-x: hidden;
	}

	/* The admin preview's parent owns vertical scrolling; customer embeds keep document scrolling. */
	html.webform-v2-preview,
	body.webform-v2-preview {
		overflow-y: hidden;
	}
</style>

<style lang="scss" scoped>
	.webform-v2-app {
		background-color: transparent;
	}

	.webform-v2-main {
		background-color: transparent;

		&.not-in-iframe {
			background-image: url("@/assets/ls-contact-loader-wallpaper-v2.svg");
			background-size: cover;
			background-position: center;
			background-color: black;
			min-height: 100vh;
		}
	}

	.form-container {
		box-sizing: border-box;

		&.not-in-iframe {
			max-width: 600px;
		}
	}

	.form-card {
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
		background-color: var(--form-background-color, #ffffff);
		color: var(--form-text-color, #212121);
		--form-placeholder-color: #9e9e9e;
		font-family: var(--form-font-family, Inter, system-ui, sans-serif);
		font-size: var(--form-font-size, 1rem);
		border-radius: var(--form-form-border-radius, 16px);
		* {
			font-family: var(--form-font-family, Inter, system-ui, sans-serif) !important;
		}
	}

	.form-card :deep(input::placeholder),
	.form-card :deep(textarea::placeholder) {
		color: var(--form-placeholder-color, #9e9e9e) !important;
		opacity: 1;
	}

	.form-logo {
		&.logo-left {
			text-align: left;
		}
		&.logo-center {
			text-align: center;
		}
		&.logo-right {
			text-align: right;
		}

		img {
			max-height: 60px;
			max-width: 200px;
			object-fit: contain;
		}
	}

	.form-header-content {
		color: inherit;
		font-family: inherit;

		:deep(*) {
			color: inherit;
			font-family: inherit;
		}

		:deep(p) {
			margin: 0 0 8px 0;
			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	.form-title {
		font-size: var(--form-heading-size, 1.5rem);
		font-weight: 400;
		color: inherit;
	}

	.form-fields {
		gap: var(--form-field-spacing, 20px);
	}

	.field-label {
		font-size: var(--form-label-font-size, 0.875rem);
		font-weight: 500;
		color: inherit;
	}

	.required-indicator {
		color: var(--form-error-text-color, #f44336);
	}

	.field-input {
		width: 100%;
		padding: var(--form-field-padding, 12px 16px);
		border: var(--form-border-width, 1px) solid var(--form-field-border-color, #e0e0e0);
		border-radius: var(--form-border-radius, 8px);
		background-color: var(--form-field-background, #ffffff);
		color: inherit;
		font-family: inherit;
		font-size: inherit;
		outline: none;
		transition: border-color 0.15s ease;
		box-sizing: border-box;

		&:focus {
			border-color: var(--form-primary-color, #1976d2);
		}

		&::placeholder {
			color: var(--form-placeholder-color, #9e9e9e);
			opacity: 1;
		}

		&.field-error {
			border-color: var(--form-error-text-color, #f44336);
		}
	}

	/*
	 * LSP-16453: iOS Safari gives <input type="date"> an intrinsic native width and
	 * ignores width:100% / min-width:0 until -webkit-appearance is reset. Without
	 * this, the native control keeps its
	 * content width and overflows the form card on iPhone. The inner value span also
	 * carries a default margin that pads the box out, so zero it too.
	 */
	.field-input[type="date"] {
		-webkit-appearance: none;
		appearance: none;
		min-width: 0;
		max-width: 100%;
	}

	.field-input[type="date"]::-webkit-date-and-time-value {
		margin: 0;
		text-align: left;
		/*
		 * LSP-16453: with -webkit-appearance:none an EMPTY iOS date input collapses
		 * the value span to zero height, so the field renders shorter than text
		 * fields until a date is picked. Reserve exactly one line so empty == filled
		 * == other fields. `1lh` (Safari 16.4+) is exact; em is the fallback.
		 */
		min-height: 1.2em;
		min-height: 1lh;
	}

	/* max-height mirrors the app-wide VTextarea default (maxHeight="300" in plugins/vuetify.js);
	   this native <textarea> does not get that default, so the resize handle was unbounded (LSP-21247). */
	.field-textarea {
		resize: vertical;
		min-height: 80px;
		max-height: 300px;
	}

	.select-wrapper {
		position: relative;
		width: 100%;
		min-width: 0;
	}

	.form-select {
		width: 100%;
		cursor: pointer;
		font-family: inherit;

		:deep(.v-field) {
			--v-field-padding-start: var(--form-field-padding-inline);
			--v-field-padding-end: var(--form-field-padding-inline);
			--v-field-padding-top: var(--form-field-padding-block);
			--v-field-padding-bottom: var(--form-field-padding-block);
			--v-input-padding-top: 0px;
			background-color: var(--form-field-background, #ffffff);
			border-radius: var(--form-border-radius, 8px);
			color: inherit;
			font-family: inherit;
			font-size: inherit;
		}

		/* Vuetify's outlined VField has a 56px minimum and a fixed 1.5rem fallback.
		 * Replace both with the native field's line box, padding, and two border widths
		 * so every form style preset renders select controls at the same size as inputs. */
		:deep(.v-field__input) {
			min-height: calc(
				1.2em + var(--form-field-padding-block) + var(--form-field-padding-block) + var(--form-border-width) +
					var(--form-border-width)
			);
			min-height: calc(
				1lh + var(--form-field-padding-block) + var(--form-field-padding-block) + var(--form-border-width) +
					var(--form-border-width)
			);
		}

		:deep(.v-field__outline) {
			--v-field-border-opacity: 1;
			--v-field-border-width: var(--form-border-width);
			color: var(--form-field-border-color, #e0e0e0);
		}

		:deep(.v-field__input) {
			color: inherit;
			font-family: inherit;
			font-size: inherit;
		}

		:deep(.v-field__input::placeholder) {
			color: var(--form-placeholder-color, #9e9e9e);
			opacity: 1;
		}

		&:deep(.v-field--focused .v-field__outline) {
			--v-field-border-opacity: 1;
			color: var(--form-primary-color, #1976d2);
		}

		&.field-error :deep(.v-field__outline) {
			--v-field-border-opacity: 1;
			color: var(--form-error-text-color, #f44336);
		}
	}

	/* The menu is teleported outside .form-card, so it needs global styling. Its
	 * width is fixed in getSelectMenuProps; wrap long labels so the menu's existing
	 * vertical scroll surface can expose the full value without widening the form. */
	:global(.form-select-menu .v-list-item-title) {
		overflow-wrap: anywhere;
		white-space: normal;
	}

	.radio-label,
	.checkbox-label {
		cursor: pointer;
		position: relative;
		padding-left: 28px;
		font-size: inherit;
		user-select: none;
	}

	.radio-input,
	.checkbox-input {
		position: absolute;
		opacity: 0;
		cursor: pointer;
		height: 0;
		width: 0;
	}

	.radio-custom,
	.checkbox-custom {
		position: absolute;
		top: 0;
		left: 0;
		height: 20px;
		width: 20px;
		background-color: #fff;
		border: var(--form-border-width, 1px) solid var(--form-field-border-color, #e0e0e0);
		border-radius: 50%;
		transition: all 0.15s ease;
	}

	.checkbox-custom {
		border-radius: 4px;
	}

	.radio-label:hover .radio-input ~ .radio-custom,
	.checkbox-label:hover .checkbox-input ~ .checkbox-custom {
		border-color: var(--form-primary-color, #1976d2);
	}

	.radio-input:checked ~ .radio-custom,
	.checkbox-input:checked ~ .checkbox-custom {
		background-color: var(--form-primary-color, #1976d2);
		border-color: var(--form-primary-color, #1976d2);
	}

	.radio-custom:after,
	.checkbox-custom:after {
		content: "";
		position: absolute;
		display: none;
	}

	.radio-input:checked ~ .radio-custom:after,
	.checkbox-input:checked ~ .checkbox-custom:after {
		display: block;
	}

	.radio-custom:after {
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: white;
	}

	.checkbox-custom:after {
		left: 8px;
		top: 4px;
		width: 4px;
		height: 8px;
		border: solid white;
		border-width: 0 2px 2px 0;
		transform: rotate(45deg);
	}

	.radio-text,
	.checkbox-text {
		color: inherit;
	}

	.clear-radio-btn {
		align-self: flex-start;
		background: none;
		border: none;
		color: #9e9e9e;
		font-size: 0.75rem;
		cursor: pointer;
		padding: 4px 0;
		margin-left: 28px; /* Align with radio text */
		text-decoration: underline;

		&:hover {
			color: #616161;
		}
	}

	.phone-field-wrapper {
		:deep(.vue-tel-input) {
			border: var(--form-border-width, 1px) solid var(--form-field-border-color, #e0e0e0);
			border-radius: var(--form-border-radius, 8px);
			background-color: var(--form-field-background, #ffffff);

			&:focus-within {
				border-color: var(--form-primary-color, #1976d2);
			}

			.vti__dropdown {
				padding: var(--form-field-padding, 12px 16px);
				padding-right: 8px;
			}

			input {
				padding: var(--form-field-padding, 12px 16px);
				padding-left: 16px;
				font-family: inherit;
				font-size: inherit;
				color: inherit;

				&::placeholder {
					color: var(--form-placeholder-color, #9e9e9e);
					opacity: 1;
				}
			}
		}

		:deep(.phone-error) {
			border-color: var(--form-error-text-color, #f44336) !important;
		}
	}

	.field-error-message {
		color: var(--form-error-text-color, #f44336);
		font-size: 0.75rem;
	}

	.field-char-counter {
		font-size: 0.75rem;
		color: var(--form-secondary-text-color, #888);
		text-align: right;
		margin-top: 4px;
	}

	.field-char-counter--over {
		color: var(--form-error-text-color, #f44336);
	}

	.submit-button {
		width: 100%;
		padding: 16px 24px;
		font-family: inherit;
		font-size: 1rem;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.15s ease;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		&.button-filled {
			background-color: var(--form-primary-color, #1976d2);
			color: var(--form-button-text-color, white);
			border: none;

			&:hover:not(:disabled) {
				filter: brightness(1.1);
			}
		}

		&.button-outlined {
			background-color: transparent;
			color: var(--form-primary-color, #1976d2);
			border: 2px solid var(--form-primary-color, #1976d2);

			&:hover:not(:disabled) {
				background-color: rgba(25, 118, 210, 0.04);
			}
		}

		&.button-text {
			background-color: transparent;
			color: var(--form-primary-color, #1976d2);
			border: none;

			&:hover:not(:disabled) {
				background-color: rgba(25, 118, 210, 0.04);
			}
		}

		&.button-radius-square {
			border-radius: 4px;
		}
		&.button-radius-rounded {
			border-radius: var(--form-button-radius, 8px);
		}
		&.button-radius-pill {
			border-radius: 48px;
		}
	}

	.form-footer-content {
		font-size: var(--form-footer-font-size, 0.875rem);
		color: inherit;
		font-family: inherit;

		:deep(*) {
			color: inherit;
			font-family: inherit;
		}

		:deep(p) {
			margin: 0 0 8px 0;
			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	// Success state
	.success-message {
		.success-header {
			text-align: left;
		}

		.success-title {
			font-size: 1.125rem;
			font-weight: 600;
			line-height: 1.4;
		}

		.success-icon {
			flex-shrink: 0;
			width: 32px;
			height: 32px;
			border-radius: 50%;
			background-color: #4caf50;
			color: white;
			font-size: 1.125rem;
		}

		.success-instruction {
			font-size: 1rem;
			color: inherit;
		}

		.qr-code-container {
			background-color: white;
			border-radius: 8px;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
		}

		.start-recording-button {
			width: 100%;
			max-width: 300px;
			padding: 16px 24px;
			font-family: inherit;
			font-size: 1rem;
			font-weight: 600;
			cursor: pointer;
			transition: all 0.15s ease;

			&:hover {
				filter: brightness(1.1);
			}

			&.button-filled {
				background-color: var(--form-primary-color, #1976d2);
				color: white;
				border: none;

				&:hover {
					filter: brightness(1.1);
				}
			}

			&.button-outlined {
				background-color: transparent;
				color: var(--form-primary-color, #1976d2);
				border: 2px solid var(--form-primary-color, #1976d2);

				&:hover {
					background-color: rgba(25, 118, 210, 0.04);
				}
			}

			&.button-text {
				background-color: transparent;
				color: var(--form-primary-color, #1976d2);
				border: none;

				&:hover {
					background-color: rgba(25, 118, 210, 0.04);
				}
			}

			&.button-radius-square {
				border-radius: 4px;
			}
			&.button-radius-rounded {
				border-radius: var(--form-button-radius, 8px);
			}
			&.button-radius-pill {
				border-radius: 48px;
			}
		}
	}

	// Loading state
	.loading-container {
		min-height: 100vh;
	}

	.loading-spinner {
		width: 20px;
		height: 20px;
		border: 2px solid transparent;
		border-top-color: currentColor;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;

		&.large {
			width: 40px;
			height: 40px;
			border-width: 4px;
			border-top-color: #1976d2;
		}
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	// Mobile adjustments
	@media (max-width: 600px) {
		.form-card {
			padding: 20px !important;
		}
	}
</style>
